import { graphqlPost } from "api/graphqlClient";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import Placeholder from "components/Layout/Placeholder";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { Popover } from "components/Popover/Popover";
import RecordLabel from "components/RecordLabel";
import { FeRecordLogRecord, FeRecordType } from "config/config";
import { MetaType } from "graphql/generated/graphql";
import { useCallback, useState } from "react";
import { getRelativeUrl, getTypedLowerCase } from "utils/service";
import { metaTypeList } from "utils/utils";
import style from "./LogsListing.module.scss";

export const ReferenceItem = ({
    referenceRecord,
    showLabel,
}: {
    referenceRecord: FeRecordLogRecord;
    showLabel?: boolean;
}) => {
    const {
        reference_record_by: by,
        reference_record_select: select,
        reference_record_type: recordType,
    } = referenceRecord;

    const name = showLabel ? select : undefined;

    const iconProp = metaTypeList.includes(recordType as MetaType)
        ? { icon: IconCollection.link }
        : { iconByType: getTypedLowerCase(recordType) as FeRecordType };

    return (
        <RecordLabel
            key={select}
            link={getRelativeUrl(getTypedLowerCase(recordType), select, by)}
            name={name}
            {...iconProp}
        />
    );
};

export const ReferenceListPopover = ({ id }: { id: string }) => {
    const [referenceList, setReferenceList] = useState<FeRecordLogRecord[]>([]);
    const [referenceCount, setReferenceCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [index, setIndex] = useState(0);

    const fetchData = useCallback(
        (index) => {
            if (isLoading) return;

            setIsLoading(true);

            const limit = 20;

            const params = {
                query: `query ($id: String!, $limit: Int, $order: String, $offset: Int) {
                            record_log(id: $id) {
                                record_log_record_list_record_log_id (limit:$limit, order:$order,offset:$offset) {
                                    record_log_record_count
                                    record_log_record_list {
                                        reference_record_by
                                        reference_record_select
                                        reference_record_type
                                    }
                                }
                            }
                        }`,
                variables: {
                    offset: index * limit,
                    order: "+id",
                    limit,
                    id,
                },
            };
            return graphqlPost(params)
                .then((data: any) => {
                    const recordLogData =
                        data?.data?.record_log?.record_log_record_list_record_log_id;

                    setReferenceList((prevData) => [
                        ...prevData,
                        ...recordLogData.record_log_record_list,
                    ]);
                    setReferenceCount(recordLogData.record_log_record_count ?? 0);
                    setIndex((prevOffset) => prevOffset + 1);
                })
                .catch((error) =>
                    NotificationService.error(<RestErrorNotification error={error} />)
                )
                .finally(() => setIsLoading(false));
        },
        [id, isLoading]
    );

    const handleScroll = useCallback(
        (e) => {
            const { scrollTop, clientHeight, scrollHeight } = e.target;
            if (
                referenceList.length < referenceCount &&
                scrollTop + clientHeight >= scrollHeight - 20
            ) {
                fetchData(index);
            }
        },
        [fetchData, index, referenceCount, referenceList.length]
    );

    return (
        <Popover
            trigger={<Button label="Show all" variation={"link"} size="small" />}
            closeOnInsideClick={false}
            actionOnOpen={() => {
                if (index === 0) {
                    fetchData(index);
                }
            }}
            contentClassName={style.referenceListPopover}
            handleScroll={handleScroll}
        >
            <div>
                <div className={style.referenceListContainer}>
                    {referenceList.map((reference) => (
                        <div key={reference.reference_record_select}>
                            <ReferenceItem referenceRecord={reference} showLabel />
                        </div>
                    ))}
                </div>
                {isLoading ? <Placeholder.NarrowList linesNumber={5} /> : null}
            </div>
        </Popover>
    );
};
